/**
 * Aba Emergência & ASO do funcionário (E10b — integração Convenia §3.2/§3.9):
 * somente leitura — os dados chegam pelo sync (domínios "emergencia" e "aso").
 * O ASO periódico tem vencimento derivado (anual) com alerta calculado na API.
 */
import { useEffect, useState } from "react";
import { Alert, Badge, Card, Group, Table, Text, Title } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { api } from "../../api";
import { fmtData } from "../../format";
import { Carregando, Vazio } from "../../componentes/Estados";
import { TabelaDados, Th } from "../../componentes/listas";

interface Contato {
  id: string;
  nome: string;
  parentesco: string | null;
  telefone: string | null;
  celular: string | null;
  telefoneTrabalho: string | null;
  email: string | null;
  ordem: number;
}
interface Aso {
  id: string;
  dataExame: string;
  tipo: string;
  statusConvenia: boolean;
  observacao: string | null;
  vencimento: string | null;
}

export function EmergenciaAsoTab({ funcionarioId }: { funcionarioId: string }) {
  const [contatos, setContatos] = useState<Contato[] | null>(null);
  const [asos, setAsos] = useState<Aso[] | null>(null);
  const [alertaAso, setAlertaAso] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ contatos: Contato[] }>(`/funcionarios/${funcionarioId}/contatos-emergencia`),
      api.get<{ asos: Aso[]; alerta: string | null }>(`/funcionarios/${funcionarioId}/asos`),
    ])
      .then(([c, a]) => {
        setContatos(c.contatos);
        setAsos(a.asos);
        setAlertaAso(a.alerta);
      })
      .catch(setErro);
  }, [funcionarioId]);

  if (erro) return <Text c="red.7">{String(erro)}</Text>;
  if (!contatos || !asos) return <Carregando linhas={4} />;

  return (
    <>
      {alertaAso && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} mb="md">
          {alertaAso}
        </Alert>
      )}

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Contatos de emergência <Badge variant="light">{contatos.length}</Badge>
        </Title>
        {contatos.length === 0 ? (
          <Vazio texto="Nenhum contato sincronizado — rode o sync Convenia (domínio emergencia) para esta pessoa." />
        ) : (
          <TabelaDados maw={720}>
            <Table.Thead>
              <Table.Tr>
                <Th>Nome</Th>
                <Th>Parentesco</Th>
                <Th>Telefones</Th>
                <Th>E-mail</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {contatos.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>
                    <Text size="sm" fw={600}>{c.nome}</Text>
                    {c.ordem === 0 && (
                      <Badge size="xs" color="sillis" variant="light">
                        preferencial
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{c.parentesco ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="xs">
                      {[c.celular, c.telefone, c.telefoneTrabalho].filter(Boolean).join(" · ") || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{c.email ?? "—"}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          ASO (exames ocupacionais) <Badge variant="light">{asos.length}</Badge>
        </Title>
        {asos.length === 0 ? (
          <Vazio texto="Nenhum ASO sincronizado — rode o sync Convenia (domínio aso) para esta pessoa." />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Data do exame</Th>
                <Th>Motivo</Th>
                <Th>Status</Th>
                <Th>Vencimento</Th>
                <Th>Observação</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {asos.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td>
                    <Text size="sm">{fmtData(a.dataExame)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="outline" color="gray">
                      {a.tipo}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={a.statusConvenia ? "sillis" : "yellow"}>
                      {a.statusConvenia ? "registrado" : "agendado/pendente"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{a.vencimento ? fmtData(a.vencimento) : "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{a.observacao ?? "—"}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>
      <Group>
        <Text size="xs" c="dimmed">
          Dados somente leitura — a fonte é o Convenia. O vencimento do periódico é derivado (exame + 1 ano).
        </Text>
      </Group>
    </>
  );
}
